// ============================================================================
// QAWorkbench/components — AIAdvisoryBadge (v0.12 polish)
// Owner: Claude 3 (Frontend & UX).
// Purpose: surface AI advisory for a workflow. CRITICAL invariant (Annex 22):
//          the AI NEVER auto-signs. The chip opens a dialog with the summary
//          + concerns; the human still applies signature.
// URS rows: PRD §9 AI Advisory, Annex 22 PQ boundary.
// ============================================================================
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAiSuggestion, useViewAiSuggestion } from '../../../api/hooks';
import styles from './AIAdvisoryBadge.module.css';

export function AIAdvisoryBadge({ workflowId }: { workflowId: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const suggestion = useAiSuggestion(workflowId, open);
  const view = useViewAiSuggestion();

  const onOpen = () => {
    setOpen(true);
    void view.mutate({ workflowId });
  };

  useEffect(() => {
    if (!open) return;
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [open]);

  return (
    <>
      <button
        type="button"
        className={styles.chip}
        aria-label={t('qa.ai.suggestion_available_aria', { defaultValue: 'AI advisory available' })}
        title={t('qa.ai.suggestion_available', { defaultValue: 'AI advisory available — view summary' })}
        onClick={onOpen}
      >
        <span aria-hidden>✨</span>
        <span>AI</span>
      </button>
      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="ai-suggestion-heading"
          className={styles.scrim}
          onClick={(e) => {
            if (e.target === e.currentTarget) setOpen(false);
          }}
        >
          <div className={styles.dialog}>
            <header className={styles.dialogHead}>
              <h3 id="ai-suggestion-heading" className={styles.dialogTitle}>
                <span aria-hidden>✨</span>
                {t('qa.ai.suggestion_heading', { defaultValue: 'AI advisory — workflow review' })}
              </h3>
              <button
                type="button"
                className={styles.closeBtn}
                onClick={() => setOpen(false)}
                aria-label="Close dialog"
              >
                ✕
              </button>
            </header>
            <p className={styles.disclaimer} role="note">
              {t('qa.ai.disclaimer', {
                defaultValue:
                  'Advisory only — Annex 22 §non-prescriptive. The AI never auto-signs or approves; a human signature is required.',
              })}
            </p>
            {suggestion.isLoading && <p className={styles.loading}>Loading suggestion…</p>}
            {suggestion.isError && (
              <p className={styles.error} role="alert">
                Could not load AI advisory.
              </p>
            )}
            {suggestion.data && (
              <>
                <p className={styles.summary}>{suggestion.data.summary}</p>
                {suggestion.data.concerns?.length > 0 && (
                  <ul className={styles.concernsList}>
                    {suggestion.data.concerns.map((c, i) => (
                      <li key={i} className={styles.concernItem}>
                        {c}
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
            <footer className={styles.dialogFoot}>
              <button type="button" className={styles.closeAction} onClick={() => setOpen(false)}>
                {t('common.close', { defaultValue: 'Close' })}
              </button>
            </footer>
          </div>
        </div>
      )}
    </>
  );
}
